import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import Error from '../../../Components/Error';
import PartnersService from '../../../Services/PartnersService';
import OperatorService from '../../../Services/OperatorService';
import { DEFAULT_TIMEZONE } from '../../../Utils/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

function SectionLabel({ icon, children }) {
  return (
    <div
      className="col-12 mt-3 mb-1"
      style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#6c757d',
        borderBottom: '1px solid #e9ecef',
        paddingBottom: '4px',
      }}
    >
      <i className={`fa ${icon} me-1`} />
      {children}
    </div>
  );
}

function Filters({ setFilters, filters, pausedDateRange = null }) {
  const [email, setEmail] = useState(filters.email_address);
  const [paidOrdersOnly, setPaidOrdersOnly] = useState(filters.paid_orders_only);
  const [bookingReference, setBookingReference] = useState(filters.booking_reference);
  const [partnerBookingReference, setPartnerBookingReference] = useState(
    filters.partner_booking_reference,
  );
  const [phoneNumber, setPhoneNumber] = useState(filters.phone_number);
  const [customerName, setCustomerName] = useState(filters.customer_name);
  const [dateStart, setDateStart] = useState(filters.date ? filters.date[0] : '');
  const [dateEnd, setDateEnd] = useState(filters.date ? filters.date[1] : '');
  const [status, setStatus] = useState(filters.status);
  const [bookingType, setBookingType] = useState(filters.booking_type);
  const [partners, setPartners] = useState(filters.partners);
  const [operators, setOperators] = useState(filters.operators);
  const [flightNumber, setFlightNumber] = useState(filters.flight_number);
  const [operatorBookingReference, setOperatorBookingReference] = useState(
    filters.operator_booking_reference,
  );
  const [eventGroup, setEventGroup] = useState(filters.event_group);
  const [reminderFilter, setReminderFilter] = useState(
    // eslint-disable-next-line no-nested-ternary
    filters.has_overdue_reminders ? 'overdue' : (filters.has_pending_reminders ? 'pending' : ''),
  );
  // What the driver left on the booking. Opt-in like every other switch
  // here: unchecked sends nothing, so an unused filter never narrows the
  // list.
  const [hasExpenseReceipt, setHasExpenseReceipt] = useState(filters.has_expense_receipt);
  const [hasOvertime, setHasOvertime] = useState(filters.has_overtime);
  const [hasDriverTracking, setHasDriverTracking] = useState(filters.has_driver_tracking);

  const [partnersList, setPartnersList] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadIn] = useState(true);

  const fetchPartners = async () => {
    try {
      const fetchedPartners = await PartnersService.fetchPartners();
      setPartnersList(fetchedPartners?.data?.data);
    } catch (err) {
      setError(error);
    }
  };

  const fetchOperators = async () => {
    try {
      const fetchedOperators = await OperatorService.fetchOperators();
      setOperatorsList(fetchedOperators?.data?.data);
    } catch (err) {
      setError(error);
    }
  };

  const debouncedFetchPartners = debounce(() => {
    fetchPartners();
  }, 500);

  const debouncedFetchOperators = debounce(() => {
    fetchOperators();
  }, 500);

  const getMultiValues = (event) => {
    const { options } = event.target;
    const values = [];
    Array.from(options).forEach((option) => {
      if (option.selected) values.push(option.value);
    });
    return values;
  };

  const search = (event) => {
    event.preventDefault();

    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    const requestedFilters = {};
    if (email) { requestedFilters.email_address = email; }
    // snake_case, like every other filter: the API reads `paid_orders_only`
    // (Filters.interface / filterBookings). Sent as `paidOrdersOnly` this
    // checkbox has never filtered anything — it ticks, the list reloads, and
    // the server never sees it. The two lines that READ it, above and below,
    // always used the correct name, which is why it looks wired up.
    if (paidOrdersOnly) { requestedFilters.paid_orders_only = true; }

    if (dateStart && dateEnd) {
      if (new Date(dateStart) > new Date(dateEnd)) {
        setError('Date Start is larger than Date End');
        return;
      }

      const startUtc = dayjs.tz(dateStart, DEFAULT_TIMEZONE).startOf('day').utc().toISOString();
      const endUtc = dayjs.tz(dateEnd, DEFAULT_TIMEZONE).endOf('day').utc().toISOString();
      requestedFilters.date = [startUtc, endUtc];
    }

    if (status) { requestedFilters.status = status; }
    if (bookingType) { requestedFilters.booking_type = bookingType; }
    if (bookingReference) { requestedFilters.booking_reference = bookingReference; }
    if (partnerBookingReference) {
      requestedFilters.partner_booking_reference = partnerBookingReference;
    }
    if (phoneNumber) { requestedFilters.phone_number = phoneNumber; }
    if (customerName) { requestedFilters.customer_name = customerName; }
    if (partners) { requestedFilters.partners = partners; }
    if (operators) { requestedFilters.operators = operators; }
    if (flightNumber) { requestedFilters.flight_number = flightNumber; }
    if (operatorBookingReference) {
      requestedFilters.operator_booking_reference = operatorBookingReference;
    }
    if (eventGroup) { requestedFilters.event_group = eventGroup; }
    if (reminderFilter === 'overdue') {
      requestedFilters.has_overdue_reminders = true;
    } else if (reminderFilter === 'pending') {
      requestedFilters.has_pending_reminders = true;
    }
    if (hasExpenseReceipt) { requestedFilters.has_expense_receipt = true; }
    if (hasOvertime) { requestedFilters.has_overtime = true; }
    if (hasDriverTracking) { requestedFilters.has_driver_tracking = true; }

    setFilters(requestedFilters);
  };

  useEffect(() => {
    if (filters.email_address) setEmail(filters.email);
    if (filters.paid_orders_only) setPaidOrdersOnly(filters.paid_orders_only);
    if (filters.booking_reference) setBookingReference(filters.booking_reference);
    if (filters.partner_booking_reference) {
      setPartnerBookingReference(filters.partner_booking_reference);
    }
    if (filters.phone_number) setPhoneNumber(filters.phone_number);
    if (filters.customer_name) setCustomerName(filters.customer_name);
    if (filters.date) setDateStart(filters.date[0]);
    if (filters.date) setDateEnd(filters.date[1]);
    if (filters.status) setStatus(filters.status);
    if (filters.booking_type) setBookingType(filters.booking_type);
    if (filters.partners) setPartners(filters.partners);
    if (filters.operators) setOperators(filters.operators);
    if (filters.flight_number) setFlightNumber(filters.flight_number);
    if (filters.operator_booking_reference) {
      setOperatorBookingReference(filters.operator_booking_reference);
    }
    if (filters.event_group) setEventGroup(filters.event_group);
    if (filters.has_overdue_reminders) {
      setReminderFilter('overdue');
    } else if (filters.has_pending_reminders) {
      setReminderFilter('pending');
    }
    if (filters.has_expense_receipt) setHasExpenseReceipt(filters.has_expense_receipt);
    if (filters.has_overtime) setHasOvertime(filters.has_overtime);
    if (filters.has_driver_tracking) setHasDriverTracking(filters.has_driver_tracking);
  }, [filters]);

  useEffect(() => {
    debouncedFetchPartners();
    debouncedFetchOperators();

    return () => {
      debouncedFetchPartners.cancel();
      debouncedFetchOperators.cancel();
    };
  }, [loadIn]);

  return (
    <div className="row">
      <div className="mt-2 col-12 col-lg-8">
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <p className="h3 mb-0">
                <i className="fa fa-filter me-2 text-primary" />
                Filters
              </p>
            </div>
            <Error error={error} />
            <form onSubmit={search}>
              <div className="row g-2">

                {/* ── Customer ── */}
                <SectionLabel icon="fa-user">Customer</SectionLabel>

                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="customerName">
                    Passenger name
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="email">Email</label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="E-mail address"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="phoneNumber">
                    Phone number
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                {/* ── Booking Details ── */}
                <SectionLabel icon="fa-file-alt">Booking Details</SectionLabel>

                <div className="col-12 col-md-4">
                  <label className="form-label small" htmlFor="status">Status</label>
                  <select
                    multiple
                    className="form-control form-control-sm"
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(getMultiValues(e))}
                    style={{ minHeight: '88px' }}
                  >
                    <option>Confirmed</option>
                    <option>Cancelled</option>
                    <option>Request</option>
                    <option>Completed</option>
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small" htmlFor="bookingType">
                    Booking Type
                  </label>
                  <select
                    className="form-control form-control-sm"
                    id="bookingType"
                    value={bookingType || ''}
                    onChange={(e) => setBookingType(e.target.value || null)}
                  >
                    <option value="">All Types</option>
                    <option value="transfer">Transfer</option>
                    <option value="experience">Experience</option>
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small" htmlFor="eventGroup">
                    Event Group
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="eventGroup"
                    value={eventGroup}
                    onChange={(e) => setEventGroup(e.target.value)}
                    placeholder="Event group name"
                  />
                </div>

                {/* ── Date Range ── */}
                <SectionLabel icon="fa-calendar">Date Range</SectionLabel>

                {/*
                  These two boxes are the only control on the screen that a
                  running quick search overrules, so they are the only one
                  that can sit here reading like it applies when it does not.
                */}
                {pausedDateRange && (
                  <div className="col-12">
                    <div className="alert alert-warning py-1 px-2 small mb-0" role="status">
                      <i className="fa fa-info-circle me-1" />
                      Not in effect: the quick search looks at all dates. Clear
                      the search to filter by date again.
                    </div>
                  </div>
                )}

                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="dateStart">
                    From
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    id="dateStart"
                    value={dateStart}
                    onChange={(e) => setDateStart(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="dateEnd">To</label>
                  <input
                    className="form-control form-control-sm"
                    type="date"
                    id="dateEnd"
                    value={dateEnd}
                    onChange={(e) => setDateEnd(e.target.value)}
                  />
                </div>

                {/* ── References ── */}
                <SectionLabel icon="fa-hashtag">References</SectionLabel>

                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="bookingReference">
                    Booking #
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="bookingReference"
                    value={bookingReference}
                    onChange={(e) => setBookingReference(e.target.value)}
                    placeholder="Booking reference"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="flightNumber">
                    Flight #
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="flightNumber"
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                    placeholder="Flight number"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label
                    className="form-label small"
                    htmlFor="partnerBookingReference"
                  >
                    Partner Booking #
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="partnerBookingReference"
                    value={partnerBookingReference}
                    onChange={(e) => setPartnerBookingReference(e.target.value)}
                    placeholder="Partner booking reference"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label
                    className="form-label small"
                    htmlFor="operatorBookingReference"
                  >
                    Operator Booking #
                  </label>
                  <input
                    className="form-control form-control-sm"
                    type="text"
                    id="operatorBookingReference"
                    value={operatorBookingReference}
                    onChange={(e) => setOperatorBookingReference(e.target.value)}
                    placeholder="Operator booking reference"
                  />
                </div>

                {/* ── Partners & Operators ── */}
                <SectionLabel icon="fa-handshake">
                  Partners &amp; Operators
                </SectionLabel>

                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="partners">
                    Partner(s)
                  </label>
                  <select
                    multiple
                    className="form-control form-control-sm"
                    id="partners"
                    value={partners}
                    onChange={(e) => setPartners(getMultiValues(e))}
                    style={{ minHeight: '80px' }}
                  >
                    <option value="">Select a partner</option>
                    {partnersList.map((value) => (
                      <option key={value.Id} value={value.Id}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="operators">
                    Operator(s)
                  </label>
                  <select
                    multiple
                    className="form-control form-control-sm"
                    id="operators"
                    value={operators}
                    onChange={(e) => setOperators(getMultiValues(e))}
                    style={{ minHeight: '80px' }}
                  >
                    <option value="">Select an operator</option>
                    {operatorsList.map((value) => (
                      <option key={value.Id} value={value.Id}>
                        {value.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ── Other ── */}
                <SectionLabel icon="fa-sliders-h">Other</SectionLabel>

                <div className="col-12 col-md-4">
                  <label className="form-label small" htmlFor="reminderFilter">
                    Reminders
                  </label>
                  <select
                    className="form-control form-control-sm"
                    id="reminderFilter"
                    value={reminderFilter}
                    onChange={(e) => setReminderFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="overdue">Overdue reminders</option>
                    <option value="pending">Any pending reminders</option>
                  </select>
                </div>
                <div className="col-12 col-md-4 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="paidOrdersOnly"
                      checked={!!paidOrdersOnly}
                      onChange={(e) => setPaidOrdersOnly(e.target.checked)}
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="paidOrdersOnly"
                    >
                      Paid orders only
                    </label>
                  </div>
                </div>
                <div className="col-12 col-md-4 d-flex align-items-end">
                  <div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hasExpenseReceipt"
                        checked={!!hasExpenseReceipt}
                        onChange={(e) => setHasExpenseReceipt(e.target.checked)}
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="hasExpenseReceipt"
                      >
                        Has expense receipt
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hasOvertime"
                        checked={!!hasOvertime}
                        onChange={(e) => setHasOvertime(e.target.checked)}
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="hasOvertime"
                      >
                        Has overtime
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="hasDriverTracking"
                        checked={!!hasDriverTracking}
                        onChange={(e) => setHasDriverTracking(e.target.checked)}
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="hasDriverTracking"
                      >
                        Has driver tracking
                      </label>
                    </div>
                  </div>
                </div>

              </div>

              {/* ── Submit ── */}
              <div className="mt-3">
                <button type="submit" className="btn btn-primary">
                  {isLoading
                    ? <i className="fa fa-spin fa-spinner" />
                    : (
                      <>
                        <i className="fa fa-search me-1" />
                        Search
                      </>
                    )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filters;
